import { useState } from 'react';
import { SEOMeta } from '../components/common/SEOMeta';
import { SectionTitle } from '../components/common/SectionTitle';
import { Button } from '../components/common/Button';
import { useScrollReveal } from '../hooks/useScrollReveal';
import '../assets/css/pages/Contact.css';
import { showSuccessAlert } from '../hooks/use-toast';
import { AppConstant } from '../constant/AppConstant';

export default function Contact() {
  const reveal = useScrollReveal();
  // const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success'>('idle');

  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    setFormState(prev => ({
      ...prev,
      [name]: value,
    }));

    setErrors(prev => ({
      ...prev,
      [name]: '',
    }));
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formState.name.trim()) {
      newErrors.name = "Your name is required";
    }

    if (!formState.email.trim()) {
      newErrors.email = "Email address is required";
    } else if (!/^\S+@\S+\.\S+$/.test(formState.email)) {
      newErrors.email = "Invalid email address format";
    }

    if (!formState.subject.trim()) {
      newErrors.subject = "Subject is required";
    }

    if (!formState.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formState.message.length < 10) {
      newErrors.message = "Message must be at least 10 characters";
    }

    return newErrors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validate();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);

    setTimeout(() => {
      showSuccessAlert("Mail sent successfully!");

      setFormState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });

      setErrors({});
      setLoading(false);
    }, 1000);
  };

  return (
    <>
      <SEOMeta
        title="Contact Us"
        description="Get in touch with Logicraft Infotech. We are ready to discuss your next big project."
        canonical="https://www.logicraftinfotech.com/contact"
      />

      <section className="ct-hero">
        <div className="ct-hero-glow ct-hero-glow--left"></div>
        <div className="ct-hero-glow ct-hero-glow--right"></div>
        <div className="container ct-hero-inner">
          <span className="ct-hero-badge">
            <i className="fa-solid fa-headset"></i> We're Here to Help
          </span>
          <h1 className="ct-hero-title">
            Let's Build Something <span className="ct-hero-accent">Great Together</span>
          </h1>
          <p className="ct-hero-sub">
            Whether you have a project in mind, need technical consulting, or just want to say hello —
            our team is ready and excited to hear from you.
          </p>
          <div className="ct-hero-stats">
            <div className="ct-stat">
              <span className="ct-stat-num">2h</span>
              <span className="ct-stat-label">Avg. Response</span>
            </div>
            <div className="ct-stat-divider"></div>
            <div className="ct-stat">
              <span className="ct-stat-num">100%</span>
              <span className="ct-stat-label">Client Satisfaction</span>
            </div>
            <div className="ct-stat-divider"></div>
            <div className="ct-stat">
              <span className="ct-stat-num">5+</span>
              <span className="ct-stat-label">Years Experience</span>
            </div>
          </div>
        </div>
      </section>

      <div className="ct-map-wrap">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3692.188147636248!2d70.77805367589666!3d22.270861443960707!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xad309d20bed5f2d1%3A0x505ba7082dd3e49b!2sLogicraft%20Infotech!5e0!3m2!1sen!2sin!4v1782881948357!5m2!1sen!2sin"
          width="100%"
          height="520"
          style={{ border: 0, display: 'block' }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="strict-origin-when-cross-origin"
          title="Logicraft Infotech Location Map"
        />
        <div className="ct-map-bottom-fade"></div>
      </div>

      <section className="ct-main-section" ref={reveal.ref}>
        <div className={`container ${reveal.className}`}>

          <div className="ct-info-strip">
            <a href={`mailto:${AppConstant.company_email}`} className="ct-info-card">
              <div className="ct-info-icon"><i className="fa-solid fa-envelope"></i></div>
              <div className="ct-info-body">
                <div className="ct-info-label">Email Us</div>
                <div className="ct-info-val">{AppConstant.company_email}</div>
              </div>
            </a>
            <a href={`tel:${AppConstant.company_mobile}`} className="ct-info-card">
              <div className="ct-info-icon"><i className="fa-solid fa-phone"></i></div>
              <div className="ct-info-body">
                <div className="ct-info-label">Call Us</div>
                <div className="ct-info-val">{AppConstant.company_mobile}</div>
              </div>
            </a>
            <div className="ct-info-card">
              <div className="ct-info-icon"><i className="fa-solid fa-location-dot"></i></div>
              <div className="ct-info-body">
                <div className="ct-info-label">Visit Us</div>
                <div className="ct-info-val">{AppConstant.company_address}</div>
              </div>
            </div>
          </div>

          <div className="ct-form-card">
            <div className="ct-form-header">
              <h2 className="ct-form-title">Send Us a Message</h2>
              <p className="ct-form-sub">Fill out the form and we'll get back to you within 2 hours.</p>
            </div>
            <form onSubmit={handleSubmit} className="ct-form">
              <div className="ct-form-row ct-form-row--3">
                <div className="form-group">
                  <label htmlFor="name"><i className="fa-solid fa-user"></i> Your Name</label>
                  <input type="text" id="name" className="form-control" placeholder="John Doe" value={formState.name} onChange={handleChange} />
                  {errors.name && <small className="error">{errors.name}</small>}
                </div>
                <div className="form-group">
                  <label htmlFor="email"><i className="fa-solid fa-envelope"></i> Email Address</label>
                  <input type="email" id="email" className="form-control" placeholder="john@example.com" value={formState.email} onChange={handleChange} />
                  {errors.email && <small className="error">{errors.email}</small>}
                </div>
                <div className="form-group">
                  <label htmlFor="phone"><i className="fa-solid fa-phone"></i> Phone Number</label>
                  <input type="tel" id="phone" className="form-control" placeholder="+91 00000 00000" value={formState.phone} onChange={handleChange} />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="subject"><i className="fa-solid fa-clipboard"></i> Subject</label>
                <input type="text" id="subject" className="form-control" placeholder="Project Inquiry" value={formState.subject} onChange={handleChange} />
                {errors.subject && <small className="error">{errors.subject}</small>}
              </div>
              <div className="form-group">
                <label htmlFor="message"><i className="fa-solid fa-message"></i> Your Message</label>
                <textarea id="message" className="form-control" placeholder="Tell us about your project — goals, timeline, budget..." value={formState.message} onChange={handleChange}></textarea>
                {errors.message && <small className="error">{errors.message}</small>}
              </div>
              <Button type="submit" className="btn-solid" loading={loading} disabled={loading}>
                <i className="fa-solid fa-paper-plane"></i> Send Message
              </Button>
            </form>
          </div>

          <div className="ct-bottom-row">
            <div className="ct-side-card ct-side-card--cta">
              <div className="ct-side-cta-icon"><i className="fa-solid fa-rocket"></i></div>
              <h3>Ready to Start?</h3>
              <p>Let's turn your idea into a powerful digital product. Our team is waiting to collaborate with you.</p>
              <a href="tel:+919265442122" className="btn-solid">
                <i className="fa-solid fa-phone-volume"></i> Call Us Now
              </a>
            </div>

            <div className="ct-side-card">
              <h4 className="ct-side-card-title"><i className="fa-solid fa-share-nodes"></i> Follow Us</h4>
              <div className="ct-socials">
                <a href={AppConstant.facebook_link} target="_blank" rel="noopener noreferrer" className="ct-social-pill ct-social-pill--fb">
                  <i className="fa-brands fa-facebook-f"></i> Facebook
                </a>
                <a href={AppConstant.instagram_link} target="_blank" rel="noopener noreferrer" className="ct-social-pill ct-social-pill--ig">
                  <i className="fa-brands fa-instagram"></i> Instagram
                </a>
                <a href={AppConstant.whatsApp_link} target="_blank" rel="noopener noreferrer" className="ct-social-pill ct-social-pill--wa">
                  <i className="fa-brands fa-whatsapp"></i> WhatsApp
                </a>
              </div>
            </div>

            <div className="ct-side-card ct-side-card--hours">
              <h4 className="ct-side-card-title"><i className="fa-solid fa-calendar-check"></i> Office Hours</h4>
              <div className="ct-hours-list">
                <div className="ct-hours-row">
                  <span>Monday – Friday</span>
                  <span className="ct-hours-open">10 AM – 7 PM</span>
                </div>
                <div className="ct-hours-row">
                  <span>Saturday</span>
                  <span className="ct-hours-closed">Closed</span>
                </div>
                <div className="ct-hours-row">
                  <span>Sunday</span>
                  <span className="ct-hours-closed">Closed</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* <section className="section" ref={reveal.ref}>
        <div className={`container ${reveal.className}`}>
          <div className="contact-grid">

            <div className="contact-form-card">
              <h3>Send us a message</h3>
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-group full-width">
                  <label htmlFor="name">Your Name</label>
                  <input type="text" id="name" name="name" value={formState.name} className="form-control" placeholder="John Doe" onChange={handleChange} />
                  {errors.name && <small className="error">{errors.name}</small>}
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input type="email" id="email" name="email" value={formState.email} className="form-control" placeholder="john@example.com" onChange={handleChange} />
                  {errors.email && <small className="error">{errors.email}</small>}
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input type="tel" id="phone" name="phone" value={formState.phone} className="form-control" placeholder="+91 00000 00000" onChange={handleChange} />
                </div>
                <div className="form-group full-width">
                  <label htmlFor="subject">Subject</label>
                  <input type="text" id="subject" name="subject" value={formState.subject} className="form-control" placeholder="Project Inquiry" onChange={handleChange} />
                  {errors.subject && <small className="error">{errors.subject}</small>}
                </div>
                <div className="form-group full-width">
                  <label htmlFor="message">Message</label>
                  <textarea id="message" name="message" value={formState.message} className="form-control" placeholder="Tell us about your project requirements..." onChange={handleChange}></textarea>
                  {errors.message && <small className="error">{errors.message}</small>}
                </div>
                <div className="form-group full-width" style={{ marginTop: 'var(--spacing-sm)' }}>
                  <Button type="submit" className="btn-solid" loading={loading} disabled={loading}>
                    Send Message <i className="fa-solid fa-paper-plane"></i>
                  </Button>
                </div>
              </form>
            </div>

            <div className="contact-info-cards">
              <div className="contact-info-card">
                <div className="contact-icon"><i className="fa-solid fa-envelope"></i></div>
                <div className="contact-details">
                  <h3>Email Us</h3>
                  <a href={`mailto:${AppConstant.company_email}`}>{AppConstant.company_email}</a>
                </div>
              </div>
              <div className="contact-info-card">
                <div className="contact-icon"><i className="fa-solid fa-phone"></i></div>
                <div className="contact-details">
                  <h3>Call Us</h3>
                  <a href={`tel:${AppConstant.company_mobile}`}>{AppConstant.company_mobile}</a><br />
                </div>
              </div>
              <div className="contact-info-card">
                <div className="contact-icon"><i className="fa-solid fa-location-dot"></i></div>
                <div className="contact-details">
                  <h3>Visit Us</h3>
                  <p>{AppConstant.company_address}</p>
                </div>
              </div>
              <div className="contact-info-card">
                <div className="contact-icon"><i className="fa-solid fa-clock"></i></div>
                <div className="contact-details">
                  <h3>Working Hours</h3>
                  <p>Mon–Fri: 10 AM – 7 PM<br />Saturday &amp; Sunday: Closed</p>
                </div>
              </div>
            </div>

          </div>

          <div className="social-connect">
            <h3>Connect With Us</h3>
            <div className="social-btn-row">
              <a href={AppConstant.facebook_link} target="_blank" rel="noopener noreferrer" className="social-btn facebook" aria-label="Facebook">
                <i className="fa-brands fa-facebook-f"></i> Facebook
              </a>
              <a href={AppConstant.instagram_link} target="_blank" rel="noopener noreferrer" className="social-btn instagram" aria-label="Instagram">
                <i className="fa-brands fa-instagram"></i> Instagram
              </a>
              <a href={AppConstant.whatsApp_link} target="_blank" rel="noopener noreferrer" className="social-btn whatsapp" aria-label="WhatsApp">
                <i className="fa-brands fa-whatsapp"></i> WhatsApp
              </a>
            </div>
          </div>

          <div className="map-section">
            <div className="map-container">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3692.188147636248!2d70.77805367589666!3d22.270861443960707!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xad309d20bed5f2d1%3A0x505ba7082dd3e49b!2sLogicraft%20Infotech!5e0!3m2!1sen!2sin!4v1782881948357!5m2!1sen!2sin"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
                title="Logicraft Infotech Location Map"
              ></iframe>
            </div>
          </div>

        </div>
      </section> */}


    </>
  );
}
